import { RightOutlined, SearchOutlined } from "@ant-design/icons"
import { Input, Flex, Select, Tooltip, Cascader } from "antd"
import { Fragment, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { getAllWidgets, getQuestionnaireSections, getQuestionnaireSubSections, getScoringQuestions } from "../../../utils/formsHelper.js"

const scoreTypeValues = {
    section: 'Section-wise',
    subSection: 'Sub-section wise',
    question: 'Question wise'
}



function ReferenceDropdown(props) {
    const { formElements = [], handleScenarioChange, scenarioIndex, conditionIndex, selectedElement, condition, workflowSteps } = props
    const [nestedList, setNestedList] = useState([])
    const [selectedParent, setSelectedParent] = useState(null)
    const [selectedField, setSelectedField] = useState(null)
    const [selectedFieldWithOptions, setSelectedFieldWithOptions] = useState(null)
    const [fieldOptions, setFieldOptions] = useState([])
    const [searchText, setSearchText] = useState(null)
    const [selectedScoreType, setSelectedScoreType] = useState(null)
    const [selectedScoreField, setSelectedScoreField] = useState(null)
    const [scoreOptions, setScoreOptions] = useState([])
    const { t } = useTranslation();

    useEffect(() => {
        if (condition.parent === "Form field") {
            setSelectedParent("Form field")
            setSelectedField(condition.reference)
            setSelectedFieldWithOptions(null)
            setFieldOptions([])
        } else if (condition.parent === "Score") {
            setSelectedParent("Score")
            setSelectedScoreType(condition.scoreType || null)
            setSelectedScoreField(condition.reference || null)
        } else {
            setSelectedParent(condition.parent)
            setSelectedField(null)
            setSelectedFieldWithOptions(null)
            setFieldOptions([])
            setSelectedScoreType(null)
            setSelectedScoreField(null)
        }
    }, [condition, nestedList])

    useEffect(() => {
        if (!selectedScoreType) {
            setScoreOptions([])
            return
        }
        let opts = []
        if (selectedScoreType === scoreTypeValues.section) {
            opts = getQuestionnaireSections(formElements).map(section => ({ label: section.label || t('Untitled Section'), value: section.id }))
        } else if (selectedScoreType === scoreTypeValues.subSection) {
            opts = getQuestionnaireSubSections(formElements).map(section => ({ label: section.label || t('Untitled Section'), value: section.id }))
        } else if (selectedScoreType === scoreTypeValues.question) {
            opts = getScoringQuestions(formElements).map(field => {
                let reqLabel = field.label || t('Untitled Field')
                if (field.sectionLabel) {
                    reqLabel += " (" + field.sectionLabel + ")"
                }
                return { label: reqLabel, value: field.id }
            })
        }
        setScoreOptions(opts)
    }, [selectedScoreType, formElements])

    useEffect(() => {
        let formFields = getAllWidgets(formElements)
        formFields = formFields.map(field => {
            let reqLabel = field.label
            if (field.sectionLabel) {
                reqLabel += " (" + field.sectionLabel + ")"
            }
            if (field.formType === "infoForm" && field.headingLabel) {
                reqLabel += " (" + field.headingLabel + ")"
            }
            if (field.formLabel) {
                reqLabel += " - " + field.formLabel
            }
            return { ...field, label: reqLabel, value: field.id }
        })
        setNestedList(formFields)
    }, [formElements])

    const parentOptions = [
        {
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('User')}</span>
                </div>
            ),
            value: 'User'
        },
        {
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('Group')}</span>
                </div>
            ),
            value: 'Group'
        },
        {
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('Connected user')}</span>
                </div>
            ),
            value: 'connectedUser'
        },
        {
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('Workflow step')}</span>
                </div>
            ),
            value: 'Workflow step'
        },
        {
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('Form field')}</span>
                    <RightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
                </div>
            ),
            value: 'Form field'
        },
        {
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t('Score')}</span>
                    <RightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
                </div>
            ),
            value: 'Score'
        }
    ]

    const scoreTypeOptions = [
        {
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t(scoreTypeValues.section)}</span>
                    <RightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
                </div>
            ),
            value: scoreTypeValues.section
        },
        {
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t(scoreTypeValues.subSection)}</span>
                    <RightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
                </div>
            ),
            value: scoreTypeValues.subSection
        },
        {
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{t(scoreTypeValues.question)}</span>
                    <RightOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
                </div>
            ),
            value: scoreTypeValues.question
        }
    ]

    const handleClear = () => {
        setSelectedParent("")
        setSelectedField(null)
        setSelectedFieldWithOptions(null)
        setFieldOptions([])
        setSelectedScoreType(null)
        setSelectedScoreField(null)
        handleScenarioChange("parent", "", scenarioIndex, conditionIndex)
    }

    const handleParentChange = (value) => {
        setSelectedParent(value)
        setSelectedField(null)
        setSelectedFieldWithOptions(null)
        setFieldOptions([])
        setSelectedScoreType(null)
        setSelectedScoreField(null)

        if (value === "Form field" || value === "Score") {
            // Don't set the parent yet, wait for deeper selection
            // handleScenarioChange("parent", value, scenarioIndex, conditionIndex)
        } else {
            handleScenarioChange("parent", value, scenarioIndex, conditionIndex)
        }
    }

    const handleFieldChange = (value) => {
        if (value === '__back__') {
            // Go back to parent selection
            setSelectedParent(null)
            setSelectedField(null)
            setSelectedFieldWithOptions(null)
            setFieldOptions([])
            return
        }

        // Always select the field directly, regardless of whether it has options
        setSelectedField(value)
        setSelectedFieldWithOptions(null)
        setFieldOptions([])
        handleScenarioChange("formReference", ["Form field", value], scenarioIndex, conditionIndex)
    }

    const handleScoreTypeChange = (value) => {
        if (value === '__back__') {
            // Go back to parent selection
            setSelectedParent(null)
            setSelectedScoreType(null)
            setSelectedScoreField(null)
            return
        }
        setSelectedScoreType(value)
        setSelectedScoreField(null)
    }

    const handleScoreFieldChange = (value) => {
        if (value === '__back__') {
            // Go back to score type selection
            setSelectedScoreType(null)
            setSelectedScoreField(null)
            return
        }
        setSelectedScoreField(value)
        handleScenarioChange("scoreReference", [selectedScoreType, value], scenarioIndex, conditionIndex)
    }



    let filteredNestedOption = [...nestedList]
    if (searchText) {
        filteredNestedOption = nestedList.filter(o => {
            let loweredLabel = o.label.toLowerCase()
            let loweredValue = searchText.toLowerCase()
            return loweredLabel.includes(loweredValue)
        })
    }



    // If Form field is selected, show the form fields dropdown
    if (selectedParent === "Form field") {

        return (
            <Select
                showSearch
                placeholder={t('Search and select field')}
                allowClear
                autoFocus
                filterOption={(input, option) => {
                    if (option?.value === '__back__') return true; // Always show back option
                    return option?.searchText?.toLowerCase().includes(input.toLowerCase()) || false;
                }}
                optionFilterProp="searchText"
                value={selectedField}
                onChange={handleFieldChange}
                onClear={() => {
                    setSelectedParent(null)
                    setSelectedField(null)
                    setSelectedFieldWithOptions(null)
                    setFieldOptions([])
                    handleScenarioChange("parent", "", scenarioIndex, conditionIndex)
                }}
                options={[
                    // Add a back option at the top
                    {
                        label: <span style={{ color: '#1890ff', fontWeight: 'bold' }}>‹ Back to types</span>,
                        value: '__back__',
                        searchText: '' // Empty search text so it won't match searches
                    },
                    ...nestedList.map((field, optionIndex) => {
                        return {
                            key: `${String(field.value || "")}-${String(field.parentTableId || "")}-${optionIndex}`,
                            label: (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    padding: '4px 0'
                                }}>
                                    <span style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <Tooltip title={field.label}>
                                            {field.label}
                                        </Tooltip>
                                    </span>
                                </div>
                            ),
                            value: field.value,
                            searchText: field.label || '', // Add searchable text
                            // Custom display value for selected option (to prevent overflow)
                            title: field.label && field.label.length > 30 ? field.label.substring(0, 30) + '...' : (field.label || '')
                        }
                    })
                ]}
                className='condition-comparator'
                dropdownStyle={{
                    maxHeight: '250px',
                    overflowY: 'auto',
                    width: 'max-content',
                    minWidth: '300px',
                    maxWidth: '500px'
                }}
                style={{
                    minWidth: '180px',
                    maxWidth: '250px'
                }}
                notFoundContent="No matching fields found"
                optionLabelProp="label"
            />
        )
    }

    // If Score is selected but the score type (Section/Sub-section/Question wise) isn't picked yet
    if (selectedParent === "Score" && !selectedScoreType) {
        return (
            <Select
                placeholder={t("Select score type")}
                autoFocus
                value={selectedScoreType}
                onChange={handleScoreTypeChange}
                options={[
                    {
                        label: <span style={{ color: '#1890ff', fontWeight: 'bold' }}>‹ {t('Back to types')}</span>,
                        value: '__back__'
                    },
                    ...scoreTypeOptions
                ]}
                className='condition-comparator'
            />
        )
    }

    // Score type is picked - show the filtered list of sections/sub-sections/questions
    if (selectedParent === "Score" && selectedScoreType) {
        return (
            <Select
                showSearch
                placeholder={t("Search and select")}
                allowClear
                autoFocus
                filterOption={(input, option) => {
                    if (option?.value === '__back__') return true;
                    return option?.searchText?.toLowerCase().includes(input.toLowerCase()) || false;
                }}
                optionFilterProp="searchText"
                value={selectedScoreField}
                onChange={handleScoreFieldChange}
                onClear={() => {
                    setSelectedScoreType(null)
                    setSelectedScoreField(null)
                }}
                options={[
                    {
                        label: <span style={{ color: '#1890ff', fontWeight: 'bold' }}>‹ {t('Back')}</span>,
                        value: '__back__',
                        searchText: ''
                    },
                    ...scoreOptions.map((opt, optionIndex) => ({
                        key: `${String(opt.value || "")}-${optionIndex}`,
                        label: <Tooltip title={opt.label}>{opt.label}</Tooltip>,
                        value: opt.value,
                        searchText: opt.label || ''
                    }))
                ]}
                className='condition-comparator'
                dropdownStyle={{
                    maxHeight: '250px',
                    overflowY: 'auto',
                    width: 'max-content',
                    minWidth: '300px',
                    maxWidth: '500px'
                }}
                style={{
                    minWidth: '180px',
                    maxWidth: '250px'
                }}
                notFoundContent={t("No matching items found")}
                optionLabelProp="label"
            />
        )
    }

    // Default view - show the parent type selector
    return (
        <Select
            placeholder={t('Select reference')}
            allowClear
            onClear={handleClear}
            value={selectedParent}
            onChange={handleParentChange}
            options={parentOptions}
            className='condition-comparator'
        />
    )
}

export default ReferenceDropdown
